
import React from 'react'
import { 
  Box, 
  Button, 
  Chip, 
  Collapse, 
  IconButton, 
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import {
  Add as AddIcon,
  BarChart as ChartIcon,
  Delete as DeleteIcon,
  Check as DoneIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material'
import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import type { ChartConfig, StyledCurveReference, YAxisPosition } from '@/interfaces/waveChart'
import { ChartAxisManager } from './ChartAxisManager'

// Chart Title Editor Component
interface ChartTitleEditorProps {
  chartId: string
  title: string
  disabled?: boolean
  onTitleChange?: (chartId: string, newTitle: string) => void
}

function ChartTitleEditor({ chartId, title, disabled, onTitleChange }: ChartTitleEditorProps) {
  const [isEditing, setIsEditing] = React.useState(false)
  const [editValue, setEditValue] = React.useState(title)

  const handleClick = () => {
    if (disabled) return
    setIsEditing(true)
    setEditValue(title)
  }

  const handleSubmit = () => {
    const trimmedValue = editValue.trim()
    if (trimmedValue && trimmedValue !== title) {
      onTitleChange?.(chartId, trimmedValue)
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
      setEditValue(title)
    }
  }

  if (isEditing) {
    return (
      <TextField
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSubmit}
        onKeyDown={handleKeyDown}
        size="small"
        variant="standard"
        autoFocus
        sx={{ 
          minWidth: 0,
          '& .MuiInput-underline:before': {
            borderBottomColor: 'transparent'
          },
          '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottomColor: 'primary.main'
          }
        }}
      />
    )
  }

  return (
    <Typography
      variant="body2"
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': disabled ? {} : {
          textDecoration: 'underline'
        },
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1
      }}
      onClick={handleClick}
      title={title}
    >
      {title}
    </Typography>
  )
}

// Sortable Chart Item Component
interface SortableChartItemProps {
  chart: ChartConfig
  index: number
  disabled?: boolean
  isReorderMode: boolean
  onCurveClick?: (chartId: string, curveId: string) => void
  onAxisClick?: (chartId: string, axisId: YAxisPosition) => void
  onCurveMoveAxis?: (chartId: string, curveRef: StyledCurveReference, fromAxis: YAxisPosition, toAxis: YAxisPosition) => void
  onCurveRemove?: (chartId: string, curveId: string) => void
  onChartTitleChange?: (chartId: string, newTitle: string) => void
  onDeleteChart?: (chartId: string) => void
  onCurveReorder?: (chartId: string, axisId: YAxisPosition, fromIndex: number, toIndex: number) => void
}

function SortableChartItem({
  chart,
  index,
  disabled = false,
  isReorderMode,
  onCurveClick,
  onAxisClick,
  onCurveMoveAxis,
  onCurveRemove,
  onChartTitleChange,
  onDeleteChart,
  onCurveReorder
}: SortableChartItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: chart.id,
    disabled: disabled || !isReorderMode
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  const handleChartDelete = React.useCallback(() => {
    onDeleteChart?.(chart.id)
  }, [chart.id, onDeleteChart])

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{ 
        mb: 1,
        borderRadius: 1
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 1,
        backgroundColor: 'action.hover',
        borderRadius: 1,
        mb: 0.5,
        cursor: disabled ? 'default' : (isReorderMode ? 'grab' : 'default'),
        '&:active': {
          cursor: disabled ? 'default' : (isReorderMode ? 'grabbing' : 'default')
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          {isReorderMode && (
            <DragIcon 
              {...attributes}
              {...listeners}
              sx={{ 
                color: 'text.secondary',
                cursor: disabled ? 'default' : 'grab',
                touchAction: 'none'
              }} 
              fontSize="small"
            />
          )}
          
          <ChartTitleEditor 
            chartId={chart.id}
            title={chart.title || `Chart ${index + 1}`}
            disabled={disabled}
            onTitleChange={onChartTitleChange}
          />
          
          <Chip 
            label={`${chart.curveReferences.length} curves`}
            size="small"
            variant="outlined"
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={handleChartDelete}
            disabled={disabled}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={!isReorderMode}>
        <ChartAxisManager
          chart={chart}
          disabled={disabled}
          onCurveClick={onCurveClick}
          onAxisClick={onAxisClick}
          onCurveMoveAxis={onCurveMoveAxis}
          onCurveRemove={onCurveRemove}
          onCurveReorder={onCurveReorder}
        />
      </Collapse>
    </Box>
  )
}

export interface ChartsListProps {
  /** Array of chart configurations */
  charts: ChartConfig[]
  
  /** Whether the component is disabled */
  disabled?: boolean
  
  /** Callback when a curve is clicked for formatting */
  onCurveClick?: (chartId: string, curveId: string) => void
  
  /** Callback when an axis is clicked for configuration */
  onAxisClick?: (chartId: string, axisId: YAxisPosition) => void
  
  /** Callback when a new chart is requested */
  onAddChart?: () => void
  
  /** Callback when a chart is deleted */
  onDeleteChart?: (chartId: string) => void
  
  /** Callback when a curve is moved between axes */
  onCurveMoveAxis?: (chartId: string, curveRef: StyledCurveReference, fromAxis: YAxisPosition, toAxis: YAxisPosition) => void
  
  /** Callback when a curve is removed */
  onCurveRemove?: (chartId: string, curveId: string) => void
  
  /** Callback when chart title is changed */
  onChartTitleChange?: (chartId: string, newTitle: string) => void
  
  /** Callback when charts are reordered */
  onChartsReorder?: (fromIndex: number, toIndex: number) => void
  
  /** Callback when curves are reordered within an axis (z-order) */
  onCurveReorder?: (chartId: string, axisId: YAxisPosition, fromIndex: number, toIndex: number) => void
}

/**
 * Clean chart list with @dnd-kit reordering and Reorder/Done toggle
 * Charts are expanded in normal mode, collapsed in reorder mode
 */
export const ChartsList = React.memo(function ChartsList({
  charts,
  disabled = false,
  onCurveClick,
  onAxisClick,
  onAddChart,
  onDeleteChart,
  onCurveMoveAxis,
  onCurveRemove,
  onChartTitleChange,
  onChartsReorder,
  onCurveReorder
}: ChartsListProps) {
  
  // Configure sensors for better performance and mobile support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3, // 3px of movement before drag starts
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250, // 250ms delay for touch
        tolerance: 5,
      },
    })
  )

  // Reorder mode and drag overlay state
  const [isReorderMode, setIsReorderMode] = React.useState(false)
  const [activeChart, setActiveChart] = React.useState<ChartConfig | null>(null)

  // Handle drag start for overlay
  const handleDragStart = React.useCallback((event: DragStartEvent) => {
    const { active } = event
    const chart = charts.find(c => c.id === active.id)
    if (chart) {
      setActiveChart(chart)
    }
  }, [charts])

  // Handle drag end and reordering
  const handleDragEnd = React.useCallback((event: DragEndEvent) => {
    const { active, over } = event
    
    setActiveChart(null)
    
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = charts.findIndex(chart => chart.id === active.id)
    const newIndex = charts.findIndex(chart => chart.id === over.id)
    
    if (oldIndex !== -1 && newIndex !== -1) {
      onChartsReorder?.(oldIndex, newIndex)
    }
  }, [charts, onChartsReorder])

  const handleChartDelete = React.useCallback((chartId: string) => {
    if (disabled) return
    onDeleteChart?.(chartId)
  }, [disabled, onDeleteChart])

  const hasMultipleCharts = charts.length > 1

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ 
          minHeight: '48px',
          px: 2,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChartIcon color="primary" />
            <Typography variant="h6">
              Charts
            </Typography>
            {charts.length > 0 && (
              <Chip 
                label={charts.length}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {hasMultipleCharts && (
                <Button
                  size="small"
                  onClick={() => setIsReorderMode(!isReorderMode)}
                  disabled={disabled}
                  variant={isReorderMode ? "contained" : "outlined"}
                  color="primary"
                  startIcon={isReorderMode ? <DoneIcon /> : undefined}
                  sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5 }}
                >
                  {isReorderMode ? "Done" : "Reorder"}
                </Button>
              )}
            
            <Tooltip title="Add Chart">
              <IconButton
                size="small"
                onClick={onAddChart}
                disabled={disabled}
                color="primary"
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Charts List */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {charts.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              color: 'text.secondary'
            }}>
              <Typography variant="body2">
                No charts yet. Add your first chart to get started.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAddChart}
                disabled={disabled}
                sx={{ mt: 2 }}
              >
                Add Chart
              </Button>
            </Box>
          ) : (
            <SortableContext 
              items={charts.map(chart => chart.id)}
              strategy={verticalListSortingStrategy}
            >
              {charts.map((chart, index) => (
                                                 <SortableChartItem
                  key={chart.id}
                  chart={chart}
                  index={index}
                  disabled={disabled}
                  isReorderMode={isReorderMode}
                  onCurveClick={onCurveClick}
                  onAxisClick={onAxisClick}
                  onCurveMoveAxis={onCurveMoveAxis}
                  onCurveRemove={onCurveRemove}
                  onChartTitleChange={onChartTitleChange}
                  onDeleteChart={handleChartDelete}
                  onCurveReorder={onCurveReorder}
                />
              ))}
            </SortableContext>
          )}
        </Box>

        {/* Drag Overlay */}
        <DragOverlay
          style={{
            zIndex: 9999,
          }}
        >
          {activeChart ? (
            <Box sx={{
              p: 1,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 1,
              boxShadow: 3,
              opacity: 0.9,
                             transform: 'rotate(2deg)',
            }}>
              <Typography variant="body2" fontWeight="bold">
                {activeChart.title || `Chart ${charts.findIndex(c => c.id === activeChart.id) + 1}`}
              </Typography>
            </Box>
          ) : null}
        </DragOverlay>
      </Box>
    </DndContext>
  )
}) 